// Menus in the Rooms style: a button that opens one (the format bar's, field editors'), a context menu at the pointer
// (tree rows and cards), and a confirmation dialog. Keyboard as the ARIA menu pattern: arrows, Home/End, Enter or
// Space, Escape back to what opened it, a letter jumps to the next item starting with it; the focused item is scrolled
// into view when a menu is taller than the window, and fades with chevrons at the edges say there's more. Colours
// (options, inks) are dots set through the CSSOM, never a style attribute (the CSP allows only the app's own style
// sheets). A section folds a group in place (New from Template): choosing it, or →/←, opens and closes it.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type MenuEntry =
  | { kind: 'separator' }
  | { kind: 'heading'; label: string }
  /** A group folded under one item, opened in place (the Mac's submenu); folded again each time the menu opens. */
  | { kind: 'section'; label: string; entries: MenuEntry[] }
  | {
      kind?: 'item' | 'radio' | 'check';
      label: string;
      onSelect: () => void;
      checked?: boolean;
      disabled?: boolean;
      /** A CSS colour for the item's dot. */
      color?: string | null;
      /** A glyph drawn before the label (list markers, alignment). */
      glyph?: string;
      shortcut?: string;
      danger?: boolean;
      /** Drawn before the label in place of the check, dot and glyph (a tile); the check then goes at the end. */
      lead?: ReactNode;
      /** Faint words at the end (a count). */
      detail?: string;
      /** In the accent colour (New Collection…). */
      accent?: boolean;
      /** Choosing it leaves the menu open (a section that folds); `expanded` says whether it's open. */
      keepOpen?: boolean;
      expanded?: boolean;
    };

type Actionable = Extract<MenuEntry, { label: string; onSelect: () => void }>;
const actionable = (e: MenuEntry): e is Actionable => e.kind !== 'separator' && e.kind !== 'heading' && e.kind !== 'section';

export function Dot({ color, className = 'dot' }: { color: string | null | undefined; className?: string }) {
  return <span className={color ? className : `${className} none`} aria-hidden="true" ref={(el) => void (color ? el?.style.setProperty('--dot', color) : el?.style.removeProperty('--dot'))} />;
}

/** Says on a scrolling menu whether items are hidden above or below (`data-more-above`/`-below`): the fade and chevron
 *  at that edge, as overlay scroll bars show nothing until the menu is scrolled. */
function markMenuOverflow(el: HTMLElement | null) {
  if (!el) return;
  el.toggleAttribute('data-more-above', el.scrollTop > 1);
  el.toggleAttribute('data-more-below', el.scrollTop + el.clientHeight < el.scrollHeight - 1);
}

/** Focuses an item and scrolls it clear of the edge fades (the menu's `scroll-padding`). */
function focusItem(el: HTMLElement | null | undefined) {
  if (!el) return;
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'nearest' });
}

/** A menu positioned at a point (fixed, kept on screen), focused on open. */
export function MenuPopup({
  entries,
  at,
  label,
  onClose,
  id,
  opener,
  className,
}: {
  entries: MenuEntry[];
  /** Where it opens; `minWidth` at least as wide as what opened it. */
  at: { x: number; y: number; alignRight?: boolean; minWidth?: number };
  label: string;
  onClose: (restoreFocus: boolean) => void;
  id?: string;
  /** The button that opened it: pressing it again toggles the menu, so the outside-press rule leaves it alone. */
  opener?: HTMLElement | null;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const markOverflow = () => markMenuOverflow(ref.current);
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(() => new Set());
  /** The section whose item takes the focus after the next render (← from inside it). */
  const refocus = useRef<string | null>(null);
  const fold = (section: string, open: boolean) =>
    setUnfolded((was) => {
      if (was.has(section) === open) return was;
      const next = new Set(was);
      if (open) next.add(section);
      else next.delete(section);
      return next;
    });
  // Placed on every render, so a section that folds open stays on screen. A menu may use the whole window less 8 px at
  // each edge: below the point if it fits, else moved up until it does. One that would scroll even then keeps to its
  // point and scrolls in the room below it, if that's at least half the window (a long menu covering its own button
  // reads as something else); only then, or in a window shorter than the menu, does a menu scroll.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (at.minWidth) el.style.setProperty('min-width', `${Math.round(at.minWidth)}px`);
    el.style.removeProperty('max-height');
    const r = el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth, vh = window.innerHeight;
    let x = at.alignRight ? at.x - r.width : at.x;
    let y = at.y;
    if (x + r.width > vw - 8) x = vw - 8 - r.width;
    const below = vh - 8 - y;
    if (r.height > below) {
      if (el.scrollHeight > el.clientHeight && below >= vh / 2) el.style.setProperty('max-height', `${Math.floor(below)}px`);
      else y = Math.max(8, vh - 8 - r.height);
    }
    el.style.setProperty('left', `${Math.max(8, x)}px`);
    el.style.setProperty('top', `${Math.max(8, y)}px`);
    if (refocus.current !== null) {
      focusItem(el.querySelector<HTMLElement>(`[data-section="${CSS.escape(refocus.current)}"]`));
      refocus.current = null;
    }
    markOverflow();
  });
  useLayoutEffect(() => {
    focusItem(ref.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])'));
  }, [at.x, at.y, at.alignRight]);
  useEffect(() => {
    const down = (e: PointerEvent) => {
      const target = e.composedPath()[0] as Node;
      if (!ref.current?.contains(target) && !opener?.contains(target)) onClose(false);
    };
    const blur = () => onClose(false);
    document.addEventListener('pointerdown', down, true);
    window.addEventListener('blur', blur);
    window.addEventListener('resize', blur);
    return () => {
      document.removeEventListener('pointerdown', down, true);
      window.removeEventListener('blur', blur);
      window.removeEventListener('resize', blur);
    };
  }, [onClose, opener]);

  const items = () => [...(ref.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])') ?? [])];
  const onKey = (e: React.KeyboardEvent) => {
    const all = items();
    const active = document.activeElement as HTMLElement;
    const i = all.indexOf(active);
    const go = (n: number) => focusItem(all[(n + all.length) % all.length]);
    switch (e.key) {
      case 'ArrowDown': go(i + 1); break;
      case 'ArrowUp': go(i - 1); break;
      case 'ArrowRight':
        if (active?.dataset.section !== undefined) fold(active.dataset.section, true);
        break;
      case 'ArrowLeft':
        if (active?.dataset.section !== undefined) fold(active.dataset.section, false);
        else if (active?.dataset.inSection !== undefined) {
          refocus.current = active.dataset.inSection;
          fold(active.dataset.inSection, false);
        }
        break;
      case 'Home': go(0); break;
      case 'End': go(all.length - 1); break;
      case 'Escape': onClose(true); break;
      case 'Tab': onClose(true); break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const k = e.key.toLowerCase();
          const next = [...all.slice(i + 1), ...all.slice(0, i + 1)].find((el) => el.textContent?.trim().toLowerCase().startsWith(k));
          focusItem(next);
          break;
        }
        return;
    }
    e.preventDefault();
    e.stopPropagation();
    markOverflow(); // now, not on the scroll event a frame later: the fade leaves the last item as it's reached
  };

  /** One entry; `section` names the open section it's in (indented, ← folds it). Sections don't nest. */
  const row = (e: MenuEntry, key: string, section: string | null): ReactNode => {
    const nested = section !== null ? ' nested' : '';
    if (e.kind === 'separator') return <div key={key} className={`menu-separator${nested}`} role="separator" />;
    if (e.kind === 'heading') return <div key={key} className={`menu-heading${nested}`} role="presentation">{e.label}</div>;
    if (e.kind === 'section') return null;
    const role = e.kind === 'radio' ? 'menuitemradio' : e.kind === 'check' ? 'menuitemcheckbox' : 'menuitem';
    return (
      <div
        key={key}
        role={role}
        tabIndex={-1}
        aria-checked={e.kind === 'radio' || e.kind === 'check' ? !!e.checked : undefined}
        aria-disabled={e.disabled || undefined}
        aria-expanded={e.keepOpen ? !!e.expanded : undefined}
        data-in-section={section ?? undefined}
        className={`menu-item${nested}${e.danger ? ' danger' : ''}${e.checked ? ' checked' : ''}${e.accent ? ' accent' : ''}${e.lead !== undefined ? ' rich' : ''}`}
        onClick={() => {
          if (e.disabled) return;
          if (!e.keepOpen) onClose(true);
          e.onSelect();
        }}
        onKeyDown={(ev) => {
          if ((ev.key === 'Enter' || ev.key === ' ') && !e.disabled) {
            ev.preventDefault();
            ev.stopPropagation();
            if (!e.keepOpen) onClose(true);
            e.onSelect();
          }
        }}
      >
        {e.lead !== undefined ? (
          <>
            <span className="menu-lead" aria-hidden="true">{e.lead}</span>
            <span className="menu-label">{e.label}</span>
            {e.detail ? <span className="menu-detail" aria-hidden="true">{e.detail}</span> : null}
            <span className="menu-check end" aria-hidden="true">{e.checked ? '✓' : ''}</span>
          </>
        ) : (
          <>
            <span className="menu-check" aria-hidden="true">{e.checked ? '✓' : ''}</span>
            {e.color !== undefined ? <Dot color={e.color} /> : null}
            {e.glyph ? <span className="menu-glyph" aria-hidden="true">{e.glyph}</span> : null}
            <span className="menu-label">{e.label}</span>
            {e.shortcut ? <span className="menu-shortcut" aria-hidden="true">{e.shortcut}</span> : null}
          </>
        )}
      </div>
    );
  };

  return createPortal(
    <div className={className ? `menu ${className}` : 'menu'} role="menu" aria-label={label} ref={ref} id={id} onKeyDown={onKey} onScroll={markOverflow}>
      <div className="menu-more above" aria-hidden="true" />
      {entries.flatMap((top, k) => {
        if (top.kind !== 'section') return [row(top, String(k), null)];
        const open = unfolded.has(top.label);
        return [
          <div
            key={k}
            role="menuitem"
            tabIndex={-1}
            aria-expanded={open}
            data-section={top.label}
            className="menu-item section"
            onClick={() => fold(top.label, !open)}
            onKeyDown={(ev) => {
              if (ev.key !== 'Enter' && ev.key !== ' ') return;
              ev.preventDefault();
              ev.stopPropagation();
              fold(top.label, !open);
            }}
          >
            <span className="menu-check" aria-hidden="true" />
            <span className="menu-label">{top.label}</span>
          </div>,
          ...(open ? top.entries.map((e, j) => row(e, `${k}/${j}`, top.label)) : []),
        ];
      })}
      <div className="menu-more below" aria-hidden="true" />
    </div>,
    document.body,
  );
}

/** A button that opens a menu below it. `children` is the button's face; `label` its accessible name. */
export function MenuButton({
  label,
  entries,
  children,
  className = 'menu-button',
  disabled,
  title,
  menuLabel,
}: {
  label: string;
  entries: MenuEntry[] | (() => MenuEntry[]);
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  title?: string;
  menuLabel?: string;
}) {
  const [open, setOpen] = useState<{ x: number; y: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const close = (restore: boolean) => {
    setOpen(null);
    if (restore) button.current?.focus();
  };
  const show = () => {
    const r = button.current!.getBoundingClientRect();
    setOpen({ x: r.left, y: r.bottom + 4 });
  };
  return (
    <>
      <button
        ref={button}
        type="button"
        className={className}
        aria-label={label}
        title={title ?? label}
        aria-haspopup="menu"
        aria-expanded={!!open}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => (open ? close(false) : show())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            show();
          }
        }}
      >
        {children}
      </button>
      {open ? <MenuPopup entries={typeof entries === 'function' ? entries() : entries} at={open} label={menuLabel ?? label} onClose={close} opener={button.current} /> : null}
    </>
  );
}

/** A modal confirmation (`<dialog>`): Escape or Cancel says no. */
export function ConfirmDialog({
  title,
  message,
  confirm,
  cancel = 'Cancel',
  onConfirm,
  onCancel,
}: {
  title: string;
  message: ReactNode;
  confirm: string;
  /** The cancel button's words; null for a notice with only `confirm` (Escape still closes it). */
  cancel?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="confirm"
      aria-labelledby="confirm-title"
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <h2 id="confirm-title">{title}</h2>
      <div className="confirm-message">{message}</div>
      <div className="actions">
        {cancel !== null ? (
          <button type="button" className="button secondary" onClick={onCancel}>
            {cancel}
          </button>
        ) : null}
        <button type="button" className="button primary" onClick={onConfirm} autoFocus>
          {confirm}
        </button>
      </div>
    </dialog>
  );
}
